import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const ConfirmContext = createContext(null);

/**
 * Provider that renders a shared confirm dialog for the React tree.
 * Components inside the tree can call `const { confirm } = useConfirm()`.
 *
 * This avoids creating a separate React root for every confirm call,
 * making it the preferred approach for components that already live
 * inside the provider tree.
 */
export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState({});
  const resolveRef = useRef(null);

  const confirm = useCallback(opts => {
    setOptions(opts);
    setOpen(true);

    return new Promise(resolve => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  const { title, description, confirmText = 'Sim', cancelText = 'Não', variant = 'destructive' } = options;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) {
            handleCancel();
          }
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
            <Button
              variant={variant}
              onClick={handleConfirm}
              autoFocus
            >
              {confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/**
 * Hook to access the confirm dialog from the ConfirmProvider.
 *
 * @returns {{ confirm: (options: import('@/lib/confirm').ConfirmOptions) => Promise<boolean> }}
 *
 * @example
 * const { confirm } = useConfirm();
 * const confirmed = await confirm({ title: 'Delete?', description: 'Cannot undo.' });
 */
export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}
